<template>
  <div class="inviteBulkGrid">
    <div ref="gridScroll" class="gridScroll">
      <table class="gridTable">
        <thead>
          <tr>
            <th class="rowNumCol">#</th>
            <th v-if="hasStatusColumn" class="rowStatusCol">Status</th>
            <th v-for="col in visibleColumns"
                :key="col.key"
                :class="{ requiredCol: col.required }">
              {{ col.label }}
            </th>
            <th class="actionsCol" aria-label="Delete row"></th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(row, rowIndex) in rows" :key="rowIndex" :class="{ 'row-locked': isLocked(rowIndex) }">
            <td class="rowNumCol">{{ rowIndex + 1 }}</td>
            <td v-if="hasStatusColumn"
                class="rowStatusCell"
                :class="{ 'rowStatusFailed': rowErrors[rowIndex] != null }">
              {{ rowErrors[rowIndex] ?? rowStatuses[rowIndex] }}
            </td>
            <td v-for="col in visibleColumns"
                :key="col.key"
                :class="cellClass(rowIndex, col)"
                :title="cellErrorMessage(rowIndex, col)"
                @click="beginEdit(rowIndex, col)">
              <template v-if="col.type === 'bool'">
                <input type="checkbox"
                       :checked="row[col.key] === true"
                       :disabled="isLocked(rowIndex)"
                       @click.stop
                       @change="onBoolChange(rowIndex, col, $event)" />
              </template>
              <template v-else-if="col.key === 'phone' && isEditing(rowIndex, col)">
                <PhoneNumberInput class="cellPhone"
                                  :inputId="'cellPhone_' + rowIndex"
                                  :countrySelectId="'cellPhoneCountry_' + rowIndex"
                                  :phoneNumberLabel="' '"
                                  :noMatchingCountriesLabel="'No matching countries.'"
                                  :phoneCountryCodes="phoneCountryCodes"
                                  :phoneCountryCode="editingPhoneCountry"
                                  :phoneNumber="stringValue(row.phone)"
                                  @click.stop
                                  @updatePhoneNumber="onPhoneNumberUpdate(rowIndex, $event)"
                                  @updatePhoneCountryCode="onPhoneCountryUpdate(rowIndex, $event)" />
              </template>
              <template v-else-if="col.key === 'phone'">
                <span class="cellText">{{ phoneDisplay(row) }}</span>
              </template>
              <template v-else-if="col.type === 'select'">
                <select v-if="isEditing(rowIndex, col)"
                        class="cellInput cellSelect"
                        :style="editInputStyle(displaySelectValue(row[col.key], col))"
                        :value="stringValue(row[col.key])"
                        @change="commitSelect(rowIndex, col, $event)"
                        @blur="cancelEdit"
                        @keydown.esc="cancelEdit">
                  <option value=""></option>
                  <option v-for="opt in selectOptions(col)" :key="opt.value" :value="opt.value">
                    {{ opt.label }}
                  </option>
                </select>
                <span v-else class="cellText">{{ displaySelectValue(row[col.key], col) }}</span>
              </template>
              <template v-else-if="isEditing(rowIndex, col)">
                <input class="cellInput"
                       :type="col.type === 'date' ? 'date' : 'text'"
                       :inputmode="col.type === 'number' ? 'numeric' : undefined"
                       :style="editInputStyle(editingDraft)"
                       :value="editingDraft"
                       @input="onDraftInput"
                       @blur="commitEdit(rowIndex, col, $event)"
                       @keydown.enter.prevent="commitEdit(rowIndex, col, $event)"
                       @keydown.esc="cancelEdit" />
              </template>
              <template v-else>
                <span class="cellText">{{ displayValue(row[col.key]) }}</span>
              </template>
            </td>
            <td class="actionsCol">
              <button type="button"
                      class="deleteRowBtn"
                      title="Delete row"
                      aria-label="Delete row"
                      @click="$emit('delete-row', rowIndex)">
                <font-awesome-icon icon="fa-solid fa-trash" />
              </button>
            </td>
          </tr>
          <tr v-if="rows.length === 0">
            <td :colspan="columnCount" class="emptyRow">
              No rows yet. Upload a CSV or add an invite to begin.
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script lang="ts">
import * as Vue from 'vue';
import { PropType } from 'vue';
import { InviteApplicationOption, InviteRow, InviteRowStatus } from '@/types/adminInvites';
import { InviteColumn, VisibleInviteColumns } from '@/constants/inviteColumns';
import PhoneNumberInput from '@/components/PhoneNumberInput.vue';
import { CountryCode, PhoneCountryCodes } from '@/types/phoneCountryCodes';
import { displayInvitePhone, resolvePhoneCountry } from '../services/invitePhone';

// One dropdown choice for a 'select' cell: the value written to the row and the label shown in the list.
interface CellSelectOption {
  value: string;
  label: string;
}

// MM-42243: the bespoke "Excel-like" bulk-invite grid (req #2, #5). A cell shows its text until clicked, then swaps to
// an input that commits on blur or Enter (checkbox cells toggle immediately); the editing input grows with its content
// up to the grid's visible width. A per-row delete button trims the batch (adding rows is the page's header action).
// The grid owns NO data: rows are a prop and every change is emitted (`cell-change`, `delete-row`) for the page to
// apply, keeping one source of truth. Cells listed in `cellErrors` (keyed "<rowIndex>|<columnKey>") render with the
// .cell-invalid highlight and the message as a tooltip (req #4). After a submit, the Status column (second, beside the
// row number) shows each row's outcome: "Created" or "Updated" for a saved row (read-only), or the failure reason from
// `rowErrors` in red for a row to fix and resubmit.
export default Vue.defineComponent({
  name: 'InviteBulkGrid',
  components: {
    PhoneNumberInput
  },
  props: {
    rows: {
      type: Array as PropType<InviteRow[]>,
      required: true
    },
    // Map of "<rowIndex>|<columnKey>" -> human-readable reason, for cells to highlight as invalid.
    cellErrors: {
      type: Object as PropType<Record<string, string>>,
      required: false,
      default: () => ({})
    },
    // Map of rowIndex -> server failure reason, shown in red in the Status column after a submit (req #7).
    rowErrors: {
      type: Object as PropType<Record<number, string>>,
      required: false,
      default: () => ({})
    },
    // Map of rowIndex -> outcome of a saved row ("Created" or "Updated"), shown in the Status column. Saved rows are
    // read-only.
    rowStatuses: {
      type: Object as PropType<Record<number, InviteRowStatus>>,
      required: false,
      default: () => ({})
    },
    // Source App options (from the applications lookup) backing the 'select' cell on the sourceApp column.
    applications: {
      type: Array as PropType<InviteApplicationOption[]>,
      required: false,
      default: () => []
    }
  },
  emits: {
    'cell-change': (_payload: { rowIndex: number; key: keyof InviteRow; value: string | boolean | null }) => true,
    'delete-row': (_rowIndex: number) => true
  },
  data() {
    return {
      // Grid shows only the non-hidden columns; the full InviteColumns list still drives serialization.
      visibleColumns: VisibleInviteColumns as InviteColumn[],
      editing: null as { rowIndex: number; key: keyof InviteRow } | null,
      // Supported dial codes for the Phone Number cell's phone component (sorted by ISO code like /find-invites).
      phoneCountryCodes: [...PhoneCountryCodes].sort((a, b) => a.Code > b.Code ? 1 : b.Code > a.Code ? -1 : 0) as CountryCode[],
      // The country selected in the currently-edited Phone Number cell, reconstructed from the row's stored
      // dial extension when editing begins and updated as the operator picks a country.
      editingPhoneCountry: undefined as CountryCode | undefined,
      // Live text of the cell being edited, seeded from the row value when editing begins. It is the input's bound
      // value while editing (the row value only changes on commit): binding the stale row value instead would make
      // every keystroke's re-render reset the input, undoing what was just typed or deleted.
      editingDraft: '' as string,
      // The grid's visible width (px) when editing began: the most an editing input may grow to.
      editingMaxWidth: 0 as number
    };
  },
  computed: {
    // The Status column appears once a submit has produced any outcome: a saved row's "Created"/"Updated" or a
    // failed row's reason.
    hasStatusColumn(): boolean {
      return Object.keys(this.rowErrors).length > 0 || Object.keys(this.rowStatuses).length > 0;
    },
    // # + optional Status + data columns + delete.
    columnCount(): number {
      return this.visibleColumns.length + 2 + (this.hasStatusColumn ? 1 : 0);
    }
  },
  methods: {
    errorKey(rowIndex: number, col: InviteColumn): string {
      return `${rowIndex}|${col.key}`;
    },
    cellErrorMessage(rowIndex: number, col: InviteColumn): string | undefined {
      return this.cellErrors[this.errorKey(rowIndex, col)];
    },
    cellClass(rowIndex: number, col: InviteColumn) {
      return {
        'cell-invalid': this.cellErrorMessage(rowIndex, col) != null,
        'cell-editing': this.isEditing(rowIndex, col),
        'cell-bool': col.type === 'bool'
      };
    },
    isLocked(rowIndex: number): boolean {
      return this.rowStatuses[rowIndex] != null;
    },
    isEditing(rowIndex: number, col: InviteColumn): boolean {
      return this.editing != null && this.editing.rowIndex === rowIndex && this.editing.key === col.key;
    },
    beginEdit(rowIndex: number, col: InviteColumn) {
      // Checkbox cells edit through their own change handler, and saved rows are read-only.
      if (col.type === 'bool' || this.isLocked(rowIndex) || this.isEditing(rowIndex, col))
        return;

      // The Phone Number cell opens a phone component; select the exact country the row's number belongs to (dial
      // codes are shared, so the full number and the row's CountryCode disambiguate).
      if (col.key === 'phone')
        this.editingPhoneCountry = resolvePhoneCountry(this.rows[rowIndex], this.phoneCountryCodes);

      const gridScroll = this.$refs.gridScroll as HTMLElement | undefined;
      this.editingMaxWidth = gridScroll ? gridScroll.clientWidth - 24 : 0;
      this.editingDraft = this.stringValue(this.rows[rowIndex][col.key]);
      this.editing = { rowIndex, key: col.key };
      this.$nextTick(() => {
        const el = this.$el.querySelector('.cellInput') as HTMLElement | null;
        el?.focus();
        // Bring the widened cell fully into view inside the horizontally scrolling grid.
        el?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
        // Only text inputs support select(); a 'select' dropdown does not.
        if (el instanceof HTMLInputElement)
          el.select();
      });
    },
    onDraftInput(event: Event) {
      this.editingDraft = (event.target as HTMLInputElement).value;
    },
    commitEdit(rowIndex: number, col: InviteColumn, event: Event) {
      const raw = (event.target as HTMLInputElement).value;
      const value = raw === '' ? null : raw;
      this.editing = null;
      this.$emit('cell-change', { rowIndex, key: col.key, value });
    },
    // Commit a 'select' cell: the chosen option value (empty -> null), then leave edit mode.
    commitSelect(rowIndex: number, col: InviteColumn, event: Event) {
      const raw = (event.target as HTMLSelectElement).value;
      const value = raw === '' ? null : raw;
      this.editing = null;
      this.$emit('cell-change', { rowIndex, key: col.key, value });
    },
    cancelEdit() {
      this.editing = null;
    },
    // Options for a 'select' column. sourceApp draws from the applications lookup (value = app key, label = name).
    selectOptions(col: InviteColumn): CellSelectOption[] {
      if (col.key === 'sourceApp')
        return this.applications
          .filter(app => app.key != null && app.key !== '')
          .map(app => ({ value: app.key as string, label: app.name || (app.key as string) }));
      return [];
    },
    // Idle display for a 'select' cell: show the matching option's friendly label, falling back to the raw value
    // (e.g. a SourceApp carried in from a CSV that is not a known application).
    displaySelectValue(value: unknown, col: InviteColumn): string {
      const text = this.stringValue(value);
      if (text === '')
        return '';
      const match = this.selectOptions(col).find(opt => opt.value === text);
      return match ? match.label : text;
    },
    onBoolChange(rowIndex: number, col: InviteColumn, event: Event) {
      const checked = (event.target as HTMLInputElement).checked;
      this.$emit('cell-change', { rowIndex, key: col.key, value: checked });
    },
    phoneDisplay(row: InviteRow): string {
      return displayInvitePhone(row);
    },
    // The phone component emits the (possibly reformatted) national number; mirror it into the row's phone cell.
    onPhoneNumberUpdate(rowIndex: number, phone: string) {
      const value = phone === '' ? null : phone;
      this.$emit('cell-change', { rowIndex, key: 'phone', value });
    },
    // The phone component emits the chosen country; store its dial extension in the hidden PhoneCountryCode cell.
    onPhoneCountryUpdate(rowIndex: number, country: CountryCode) {
      this.editingPhoneCountry = country;
      const ext = country?.Ext ?? '';
      this.$emit('cell-change', { rowIndex, key: 'phoneCountryCode', value: ext === '' ? null : ext });
    },
    // Size an editing cell's input to its content so the focused column expands to show the full value and keeps
    // growing as the operator types. Width tracks the character count (in ch, never below a readable minimum) and is
    // capped at the grid's visible width, beyond which the input scrolls its text.
    editInputStyle(value: string): Record<string, string> {
      const chars = Math.max(value.length + 2, 12);
      const style: Record<string, string> = { width: `${chars}ch`, minWidth: `${chars}ch` };
      if (this.editingMaxWidth > 0)
        style.maxWidth = `${this.editingMaxWidth}px`;
      return style;
    },
    stringValue(value: unknown): string {
      return value == null ? '' : String(value);
    },
    displayValue(value: unknown): string {
      return value == null ? '' : String(value);
    }
  }
});
</script>

<style lang="scss">
.inviteBulkGrid {
  .gridScroll {
    overflow-x: auto;
    border: 1px solid $medium_light_gray;
    border-radius: $element_border_radius;
  }

  .gridTable {
    border-collapse: collapse;
    white-space: nowrap;
    font-size: 0.9rem;

    th,
    td {
      border: 1px solid $gray;
      padding: 4px 8px;
      text-align: left;
      vertical-align: middle;
    }

    thead th {
      background: $very_light_gray;
      font-weight: $font_weight_bold;
      position: sticky;
      top: 0;

      &.requiredCol::after {
        content: ' *';
        color: $error_color;
      }
    }

    .rowNumCol {
      text-align: right;
      color: $faint_text;
      background: $very_light_gray;
    }

    td {
      cursor: pointer;
      min-width: 90px;

      &.cell-bool {
        text-align: center;
        cursor: default;
      }

      &.cell-invalid {
        background: #f9d7dd;
        outline: 2px solid $error_color;
        outline-offset: -2px;
      }

      .cellText {
        display: inline-block;
        min-height: 1.2em;
        min-width: 70px;
      }

      .cellInput {
        width: 100%;
        min-width: 90px;
        box-sizing: border-box;
      }
    }

    .emptyRow {
      text-align: center;
      color: $faint_text;
      cursor: default;
      padding: $standard_space;
    }

    // Shrink-to-fit: the 1% width collapses the column to its content (the icon button).
    .actionsCol {
      width: 1%;
      min-width: 0;
      padding: 4px 6px;
      text-align: center;
      cursor: default;
    }

    .deleteRowBtn {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: auto;
      min-width: 0;
      height: auto;
      padding: 2px;
      margin: 0;
      background: none;
      border: none;
      color: $error_color;
      font-size: 1rem;
      line-height: 1;
      cursor: pointer;

      &:hover {
        opacity: 0.75;
        background: none;
      }
    }

    .rowStatusCell {
      font-weight: $font_weight_bold;
      color: $primary_color;
      cursor: default;

      // A failure reason can be long: wrap it in a readable column instead of stretching the row.
      &.rowStatusFailed {
        font-weight: normal;
        color: $error_color;
        white-space: normal;
        min-width: 200px;
      }
    }

    tr.row-locked td:not(.actionsCol) {
      background: $very_light_gray;
      color: $faint_text;
      cursor: default;
    }

    tr.row-locked td.rowStatusCell {
      color: $primary_color;
    }
  }
}
</style>
